import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ExternalLinkIcon, Loader2Icon, SparklesIcon } from 'lucide-react';
import { clicksSearch, leadPost, leadsSearch, sanitizeTcpaHtml, schoolLogo, schoolName, type LeadAnswers, type LeadContext, type MatchSchool } from '../../utils/mediaMatchersApi';
import { parseBulletPoints } from '../../utils/htmlParser';

// Presented one school at a time: first the Leads results, each needing its own
// consent, then the Clicks results as lower-commitment click-throughs.
type Phase = 'loading' | 'leads' | 'clicks' | 'done';
interface SchoolMatchCarouselProps {
  answers: LeadAnswers;
  context: LeadContext;
  /** Program name shown as the reason this school was matched. */
  programLabel: string;
  /** Where "I'm done" and the end of the carousel send the visitor. */
  exitTo: string;
}
function DotStepper({
  total,
  index
}: {
  total: number;
  index: number;
}) {
  return <div className="flex items-center gap-3" data-id="element-615" data-mp="1dsg4pz7p0q85">
      <div className="flex items-center gap-1.5" aria-hidden="true" data-id="element-616" data-mp="b7gmzgs12d14">
        {Array.from({
        length: total
      }).map((_, i) => <span key={i} className={`h-2 w-2 rounded-full ${i === index ? 'bg-[#1d4ed8]' : i < index ? 'bg-[#1d4ed8]/40' : 'border border-gray-300'}`} data-id="element-617" data-mp="f9tsq56ryaxw" data-mpi={i} />)}
      </div>
      <p className="text-sm font-semibold text-gray-600" data-id="element-618" data-mp="twn08oa1ycfj">
        School {index + 1} of {total}
      </p>
    </div>;
}
function MatchCard({
  school,
  programLabel,
  variant,
  consent,
  onConsentChange,
  onPrimary,
  onSkip,
  primaryLabel
}: {
  school: MatchSchool;
  programLabel: string;
  variant: 'lead' | 'click';
  consent?: boolean;
  onConsentChange?: (value: boolean) => void;
  onPrimary: () => void;
  onSkip: () => void;
  primaryLabel: string;
}) {
  const name = schoolName(school) || 'Partner School';
  const logo = schoolLogo(school);
  const highlights = parseBulletPoints(school.bodyContent || '').slice(0, 3);
  const matchedProgram = school.programName || school.mProgramName || programLabel;
  const disclosure = sanitizeTcpaHtml(school.tcpaDisclaimer || '', 'underline text-[#1e40af] hover:text-[#17307d]');
  return <article className="rounded-xl bg-white p-6 shadow-lg md:p-8" data-id="element-619" data-mp="kvxk28n5j0oi">
      <header className="flex items-start gap-4" data-id="element-620" data-mp="kumdy268hgu8">
        {logo ? <img src={logo} alt="" className="h-12 max-w-[150px] shrink-0 object-contain" data-id="element-621" data-mp="zs6gl2fnqzx8" /> : null}
        <div className="min-w-0" data-id="element-622" data-mp="usajzo9aiarp">
          <h2 className="text-xl font-bold leading-snug text-gray-900" data-id="element-623" data-mp="18lrvqmvqij76">
            {name}
          </h2>
          {school.accreditation && <p className="mt-0.5 text-xs text-gray-500" data-id="element-624" data-mp="14m18gt5f2l7e">
              {school.accreditation}
            </p>}
        </div>
        {school.schoolHighlight && <span className="ml-auto hidden shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[#1e40af] sm:inline-flex" data-id="element-625" data-mp="1dlxoy7hnkdrd">
            <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-626" data-mp="2257t2tk0i012" />
            {school.schoolHighlight}
          </span>}
      </header>

      {(matchedProgram || school.headContent) && <div className="mt-5 rounded-lg border border-gray-200 px-4 py-3" data-id="element-627" data-mp="1s71w8qd2bk32">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-628" data-mp="1s94v7zw70njx">
            {variant === 'lead' ? 'Your matching program' : 'Program'}
          </p>
          <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-629" data-mp="1j86nb6imvvw2">
            {school.headContent || matchedProgram}
          </p>
        </div>}

      {school.aboutBrand && <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-gray-600" data-id="element-630" data-mp="19hlrtry9b7hd">
          {school.aboutBrand.replace(/<[^>]*>?/gm, '')}
        </p>}

      {highlights.length > 0 && <ul className="mt-4 space-y-2" data-id="element-631" data-mp="zhl5ry906d9h">
          {highlights.map(item => <li key={item} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-632" data-mp="sfzfksgor3vt">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#1d4ed8]" aria-hidden="true" data-id="element-633" data-mp="ngmv2edae8we" />
              <span className="truncate" data-id="element-634" data-mp="iobt75yzkd17">{item}</span>
            </li>)}
        </ul>}

      {variant === 'lead' && <label className="mt-6 flex gap-3 border-t border-gray-200 pt-5 text-xs leading-relaxed text-gray-600" data-id="element-635" data-mp="dopgqyqzf0tg">
          <input type="checkbox" checked={Boolean(consent)} onChange={e => onConsentChange?.(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-400 text-[#1e40af] focus:ring-[#1e40af]" data-id="element-636" data-mp="4pvearqyzg7z" />
          {disclosure ? <span dangerouslySetInnerHTML={{
        __html: disclosure
      }} data-id="element-637" data-mp="1u4tec9x4ut8j" /> : <span data-id="element-638" data-mp="1u2focfhb877u">
              Yes, I consent to be contacted by {name} about their programs by
              phone, text, and email, including with automated technology.
              Consent is not required to enroll.
            </span>}
        </label>}

      <div className="mt-6" data-id="element-639" data-mp="21mrjy3c71k6h">
        <button type="button" onClick={onPrimary} disabled={variant === 'lead' && !consent} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#e89b33] px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-[#17255a] transition-colors hover:bg-[#d98c22] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] focus-visible:ring-offset-2" data-id="element-640" data-mp="21lyfxa4md8s1">
          {primaryLabel}
          {variant === 'click' && <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-641" data-mp="1snwe4sd2ac3p" />}
        </button>
        <button type="button" onClick={onSkip} className="mx-auto mt-3 block text-sm text-gray-500 underline-offset-2 transition-colors hover:text-gray-800 hover:underline" data-id="element-642" data-mp="2z4i0qcla5t2v">
          Skip this school
        </button>
      </div>
    </article>;
}
export function SchoolMatchCarousel({
  answers,
  context,
  programLabel,
  exitTo
}: SchoolMatchCarouselProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [leads, setLeads] = useState<MatchSchool[]>([]);
  const [clicks, setClicks] = useState<MatchSchool[]>([]);
  const [index, setIndex] = useState(0);
  const [consent, setConsent] = useState(false);

  // Both searches run together so the second carousel is ready by the time the
  // visitor works through the first.
  useEffect(() => {
    let active = true;
    const run = async () => {
      const [leadResults, clickResults] = await Promise.all([leadsSearch(answers, context), clicksSearch(answers, context)]);
      if (!active) return;
      setLeads(leadResults);
      setClicks(clickResults);
      setPhase(leadResults.length > 0 ? 'leads' : clickResults.length > 0 ? 'clicks' : 'done');
    };
    run();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const current = useMemo(() => {
    if (phase === 'leads') return leads[index];
    if (phase === 'clicks') return clicks[index];
    return undefined;
  }, [phase, leads, clicks, index]);
  const advance = () => {
    setConsent(false);
    const list = phase === 'leads' ? leads : clicks;
    if (index + 1 < list.length) {
      setIndex(index + 1);
      return;
    }
    if (phase === 'leads' && clicks.length > 0) {
      setPhase('clicks');
      setIndex(0);
      return;
    }
    setPhase('done');
  };
  const handlePrimary = () => {
    if (!current) return;
    if (phase === 'leads') {
      leadPost(answers, context, current);
    } else {
      const url = current.clickURL || current.clickUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
    advance();
  };
  if (phase === 'loading') {
    return <div className="mx-auto max-w-xl px-4 py-24 text-center" data-id="element-643" data-mp="3dmj9ssyspt2k">
        <Loader2Icon className="mx-auto h-8 w-8 animate-spin text-[#1d4ed8]" aria-hidden="true" data-id="element-644" data-mp="38mwvshb588sp" />
        <h1 className="mt-5 text-2xl font-bold text-gray-900" data-id="element-645" data-mp="34m391r9ft01e">
          Finding your matches…
        </h1>
        <p className="mt-2 text-gray-600" data-id="element-646" data-mp="2zna5aqjycnef">
          We're checking which accredited schools are enrolling {programLabel}{' '}
          students now.
        </p>
      </div>;
  }
  if (phase === 'done' || !current) {
    return <div className="mx-auto max-w-xl px-4 py-24 text-center" data-id="element-647" data-mp="3scqs98oszc39">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50" aria-hidden="true" data-id="element-648" data-mp="3nimloqjg962p">
          <CheckIcon className="h-6 w-6 text-[#1d4ed8]" data-id="element-649" data-mp="juueboqzp57g" />
        </span>
        <h1 className="mt-5 text-2xl font-bold text-gray-900" data-id="element-650" data-mp="jwxe30hy2cpd">
          You're all set
        </h1>
        <p className="mt-2 text-gray-600" data-id="element-651" data-mp="f218exjsbqts">
          The schools you selected will reach out using the contact details you
          provided. Keep an eye on your email and phone.
        </p>
        <a href={exitTo} className="mt-8 inline-block rounded-lg bg-[#1e40af] px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#17307d]" data-id="element-652" data-mp="relrr0segmez">
          Browse more programs
        </a>
      </div>;
  }
  const list = phase === 'leads' ? leads : clicks;
  return <div className="mx-auto max-w-2xl px-4 py-10 md:py-14" data-id="element-653" data-mp="1acy38t6sa8dq">
      {phase === 'clicks' && <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#1e40af]" data-id="element-654" data-mp="1f3xy7swcegdl">
          Additional matches
        </p>}
      <h1 className="text-2xl font-bold text-gray-900 md:text-3xl" data-id="element-655" data-mp="1k1zhoexokhpl">
        {phase === 'leads' ? 'Schools matched to your answers' : 'A few more programs worth a look'}
      </h1>
      <p className="mt-2 text-gray-600" data-id="element-656" data-mp="1t4qw8nlurtlg">
        {phase === 'leads' ? 'Review each school and choose the ones you want to hear from.' : 'Open any that interest you — no contact details are shared.'}
      </p>

      <div className="mt-6" data-id="element-657" data-mp="lyqqes5hm8oy">
        <DotStepper total={list.length} index={index} data-id="element-658" data-mp="qxk4aloos1pn" />
      </div>

      {/* Remounting on the school id lets the card fade in on each advance. */}
      <div key={`${phase}-${index}`} className="mt-4 motion-safe:animate-[fadeInUp_180ms_cubic-bezier(0.23,1,0.32,1)]" data-id="element-659" data-mp="vp277fa9scd9">
        <MatchCard school={current} programLabel={programLabel} variant={phase === 'leads' ? 'lead' : 'click'} consent={consent} onConsentChange={setConsent} onPrimary={handlePrimary} onSkip={advance} primaryLabel={phase === 'leads' ? 'Yes, contact me' : 'View this program'} data-id="element-660" data-mp="zs8cajlnbxi5" />
      </div>

      <button type="button" onClick={() => setPhase('done')} className="mx-auto mt-6 block text-sm text-gray-500 underline-offset-2 transition-colors hover:text-gray-800 hover:underline" data-id="element-661" data-mp="3gehrrsiwb4ug">
        Skip all remaining →
      </button>
    </div>;
}